import React, { useState, cloneElement, useRef } from "react"
import cn from "classnames"
import PropTypes from "prop-types"
import { FloatingArrow, FloatingPortal } from "@floating-ui/react"
import { Text } from "@asphalt-react/typography"

import { usePopover } from "./usePopover.js"
import { BasePopover } from "./base-popover.js"
import { ARROW_SIZE } from "./helper"
import styles from "./tooltip.module.css"

export const Tooltip = ({
  target,
  children,
  arrow = true,
  placement = "bottom",
  size = "m",
  title,
  bezel = true,
  ...props
}) => {
  // filtering out style and className
  /* eslint-disable-next-line react/prop-types */
  const { style, className, ...rest } = props

  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)
  const { getTargetProps, getPopoverProps, getFocusProps } = usePopover({
    open,
    onOpenChange: setOpen,
    isHover: true,
    isClick: false,
    arrowRef: arrow ? arrowRef : null,
    placement,
    flip: true,
    shift: true,
    bezel,
  })
  const { popoverContext } = getFocusProps()

  const classes = cn(styles.Tooltip, styles[size])

  // Override arrow offset from the floating element edge if placement is bottom/top start/end.
  // If not added, arrow will be kept in the center of target element.
  const arrowOffset =
    /bottom|top/u.test(placement) && /start|end/u.test(placement)
      ? { staticOffset: `${ARROW_SIZE}px` }
      : {}

  return (
    <>
      {cloneElement(target, { ...getTargetProps() })}
      {open && (
        <FloatingPortal>
          <BasePopover {...rest} inverse {...getPopoverProps()} role="tooltip">
            <span className={classes}>
              {title ? (
                <Text div onBrand bold size={size}>
                  {title}
                </Text>
              ) : null}
              <Text span size={size} onBrand>
                {children}
              </Text>
            </span>
            {arrow ? (
              <FloatingArrow
                ref={arrowRef}
                context={popoverContext}
                className={styles.arrow}
                tipRadius={2}
                {...arrowOffset}
              />
            ) : null}
          </BasePopover>
        </FloatingPortal>
      )}
    </>
  )
}

Tooltip.displayName = "Tooltip"

Tooltip.propTypes = {
  /**
   * Tooltip content receives node.
   */
  children: PropTypes.node.isRequired,
  /**
   * Target node that triggers the Popover.
   */
  target: PropTypes.node.isRequired,
  /**
   * Tooltip title, only receives string.
   */
  title: PropTypes.string,
  /**
   * Tooltip size, can be "small", "medium" or "large".
   */
  size: PropTypes.oneOf(["s", "m", "l"]),
  /**
   * Set `arrow=false` to disable arrow.
   */
  arrow: PropTypes.bool,
  /**
   * Placement of the Popover with respect to target node.
   *
   * Popover supports multiple placements:
   * * `top` • `top-start` • `top-end`
   * * `bottom` • `bottom-start` • `bottom-end`
   * * `left` • `left-start` • `left-end`
   * * `right` • `right-start` • `right-end`
   */
  placement: PropTypes.oneOf([
    "top",
    "top-start",
    "top-end",
    "bottom",
    "bottom-start",
    "bottom-end",
    "right",
    "right-start",
    "right-end",
    "left",
    "left-start",
    "left-end",
  ]),
  /**
   * Adds padding on all sides.
   */
  bezel: PropTypes.bool,
}
